/**
 * One verdict on the connected WhatsApp account, derived once, drawn twice.
 *
 * ## Why this file exists
 *
 * Slice 16 turns `/integrations` into a tab per channel, and the one real cost
 * of tabs is that a problem on the tab you are not looking at is a problem you
 * cannot see. Stacked cards at least scrolled past it. So the tab rail has to
 * carry a status of its own, and the moment two places describe the same
 * account, they can disagree — which on this screen means a green dot over an
 * expired token.
 *
 * They cannot disagree here, because there is one derivation. The panel header's
 * badge and the rail's dot are the **same** {@link WhatsAppTabStatus} value; the
 * panel simply also renders the reasons. Adding a state means editing one
 * cascade, and both surfaces move together.
 *
 * ## The verdicts are `@wend/shared`'s, not this file's
 *
 * `metaReadiness()` and `metaTokenExpiry()` are imported and called, never
 * re-implemented. The API calls the same two functions to set the row's status
 * and to decide whether a WhatsApp campaign may be scheduled at all, so a
 * browser that worked the answer out for itself is a browser that can tell
 * somebody they are ready to send while the scheduler refuses them.
 *
 * **This replaces a local copy.** `WhatsAppCard.tsx` carried a private
 * `storedExpiryState()` that reimplemented `metaTokenExpiry` line for line,
 * including the fourteen-day threshold, with a comment noting the two had to
 * stay in step. They were in step; the point is that nothing made them stay
 * that way, and the threshold is a constant somebody will tune once.
 *
 * ## What the card could not see before, and now can
 *
 * The old card consulted `readiness` **only when this session had pressed
 * Test**. On a first paint it showed the token expiry and nothing else — so a
 * number Meta has not verified, or a number Meta has rated red, was invisible
 * until somebody thought to re-check an account that looked connected. That was
 * survivable when the card was on screen with everything else. It is not
 * survivable behind a tab: the dot would be reporting on a verdict the page had
 * not bothered to compute.
 *
 * So readiness is derived from `integration.lastCheck` on load, exactly the way
 * `readSesIntegration` derives it from an SES snapshot. Deriving it is free; the
 * snapshot is already on the row.
 *
 * ## Stale is a state, and it beats green
 *
 * A re-check that fails leaves the last successful snapshot in place on purpose:
 * "failing now, last verified on Tuesday" is true and useful. But a verdict
 * computed from that snapshot describes a moment that has passed, so `stale`
 * marks it and the cascade below puts `failing` above every green thing that
 * snapshot could still be claiming.
 *
 * ## The order of the cascade is the whole design
 *
 * Ordered by *what stops a message today*, then by *what stops one soonest* —
 * not by severity in the abstract:
 *
 *   1. an **expired token** outranks a failed check, because it names the
 *      repair. "Not working" does not;
 *   2. a **failed check** outranks every verdict derived from an older snapshot;
 *   3. **a stub having answered** outranks every verdict that is not already
 *      red. This one shipped wrong and is the reason the rule is written down:
 *      the cascade read `readiness`, `expiry` and `expiryKnown` and never read
 *      `check.transport`, so a stub-answered check with healthy fields fell all
 *      the way through to `ok` and painted a green tick on the rail and a green
 *      "Ready to send" in the panel header — three lines above the panel's own
 *      `META_STUB_NOTICE`, which says everything above it is fabricated and
 *      nothing can be sent on this connection. Lying by adjacency is the defect
 *      class this repository keeps catching, and a badge a centimetre from the
 *      sentence contradicting it is exactly that shape. It sits *below* the two
 *      red branches rather than above them because those two do not contradict
 *      the panel — the panel renders both the expiry callout and the stub
 *      callout, and "Token expired" names a repair where "Simulated" does not —
 *      but it sits above every amber and green branch, because each of those is
 *      a claim about an account nobody spoke to;
 *   4. **never tested** is amber, never green. A connect runs a check, so it
 *      should not happen — and a card that assumed it could not would paint a
 *      green badge out of an empty snapshot on the day it did;
 *   5. `numberVerified` and `qualityFlagged` are read as **flags, not as
 *      `readiness.state`**, which is what `metaReadiness`'s own header asks for:
 *      the state is the headline and the flags are the full story, and a red
 *      quality rating hidden behind an unverified number is a rating nobody
 *      sees;
 *   6. **an expiry Meta would not report is amber, not green.** `expiresAt` is
 *      null both for a token that never expires and for one `debug_token`
 *      declined to describe. `metaTokenInfoSchema`'s own comment requires a
 *      screen to tell those apart rather than paint a green permanent token —
 *      and the second is precisely the case where this panel's warning cannot
 *      fire, which is the failure the panel exists to prevent.
 *
 * And one invariant underneath all six, which was got wrong first: **the expiry
 * verdict and the date it is about come from the same place.** Taking
 * `readiness.tokenExpiry` — derived from `lastCheck` — while printing
 * `integration.tokenExpiresAt` renders an expired date under the words "no
 * expiry" as soon as those two disagree. `verify-whatsapp-tab.tsx` caught it;
 * the comment beside the computation says how it is now prevented.
 *
 * ## Why there is no request in this file
 *
 * Every import here is either `@wend/shared`, a display map, or a type — and the
 * type import of `WhatsAppIntegration` is erased. That is not tidiness. A module
 * that pulls in `lib/api/client` pulls in `lib/config.ts`, which reads
 * `import.meta.env` and evaluates only inside Vite, and a module that does *that*
 * cannot be imported by a `verify-*` script. This cascade is the thing on the
 * WhatsApp tab most worth asserting and least reachable by a compiler, so it is
 * kept where `verify-whatsapp-tab.tsx` can reach it.
 *
 * There is no cache and no module-level state here either: anything remembered at
 * module scope on a multi-tenant front-end is one workspace's account rendered
 * under another's name.
 */
import {
  metaReadiness,
  metaTokenExpiry,
  type MetaCheckOutcome,
  type MetaCheckResult,
  type MetaReadiness,
  type MetaTokenExpiryState,
} from '@wend/shared';
import type { BadgeTone } from '@wend/ui';

import type { WhatsAppIntegration } from '../../lib/api/whatsapp';
import {
  META_READINESS_DISPLAY,
  META_STUB_NOTICE,
  META_TOKEN_EXPIRY_DISPLAY,
} from '../../lib/whatsapp-display';

/* -------------------------------------------------------------------------- */
/* The reading                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * What a check this session learned, when it was this session that asked.
 *
 * Kept apart from the stored row because it is fresher than anything on it: the
 * API answers a connect and a Test with the outcome *and* its own readiness, and
 * that readiness is the one the scheduler will act on.
 */
export interface WhatsAppSessionCheck {
  readonly outcome: MetaCheckOutcome;
  readonly readiness: MetaReadiness | null;
}

/** Everything the WhatsApp tab needs in order to decide what to draw. */
export interface WhatsAppReading {
  /** A row exists: this workspace has connected a Meta account. */
  readonly connected: boolean;
  /** The last check did not authenticate, or never reached Meta. */
  readonly failing: boolean;
  /**
   * The snapshot every figure on the panel is read from: this session's check
   * when there is one, otherwise the last **successful** check on the row.
   * `null` until one has ever succeeded — which is not the same as zero.
   */
  readonly check: MetaCheckResult | null;
  /** There is a snapshot and it is history: label the figures, do not hide them. */
  readonly stale: boolean;
  /** `@wend/shared`'s verdict on that snapshot. `null` when there is none. */
  readonly readiness: MetaReadiness | null;
  /** When the token stops working. `null` for "never" *and* for "not reported". */
  readonly expiresAt: string | null;
  /**
   * The verdict on `expiresAt` — and always on *that* value, never on a date
   * from somewhere else. See the comment where it is computed.
   *
   * Note that `never` means "nothing you need to act on", which includes a token
   * that expires in six months. It is **not** a synonym for "has no expiry", and
   * a screen that renders it as one is making a claim about a token that does in
   * fact stop working.
   */
  readonly expiry: MetaTokenExpiryState;
  /**
   * Whether the expiry is known at all — a date in hand, or `debug_token` having
   * answered and reported none.
   *
   * `false` is the gap that matters: `expiry` then reads `never` only because
   * nothing said otherwise, and a screen must not turn that into good news.
   * `debug_token` is the one call in a check allowed to fail without failing the
   * check, so this is reachable on a connection that is otherwise perfect.
   */
  readonly expiryKnown: boolean;
}

/**
 * Derives the reading from the stored row and this session's check, if any.
 *
 * `sessionCheck` wins wherever it has an opinion, including when its opinion is
 * a failure: a Test that has just been refused must not leave the panel showing
 * the verdict from the snapshot it failed to replace.
 */
export function readWhatsAppIntegration(
  integration: WhatsAppIntegration | null,
  sessionCheck: WhatsAppSessionCheck | null,
): WhatsAppReading {
  if (integration === null) {
    return {
      connected: false,
      failing: false,
      check: null,
      stale: false,
      readiness: null,
      expiresAt: null,
      expiry: 'never',
      expiryKnown: false,
    };
  }

  const fresh = sessionCheck?.outcome.ok === true ? sessionCheck.outcome.result : null;
  const check = fresh ?? integration.lastCheck;
  const failing =
    sessionCheck === null ? integration.status === 'failed' : !sessionCheck.outcome.ok;

  /*
   * A readiness derived from a stale snapshot is still worth having — the
   * panel labels it rather than hiding it — but `failing` sits above every
   * green state in the cascade below, so it can never paint the tab green.
   */
  const readiness =
    sessionCheck?.readiness ?? (check === null ? null : metaReadiness(check));

  // The row's own `tokenExpiresAt` is the fallback rather than the preference:
  // a fresh check has just read the token, and the row is written from it.
  const expiresAt = fresh?.token.expiresAt ?? integration.tokenExpiresAt;

  /*
   * **The verdict and the date it is about must come from the same place.**
   *
   * `readiness.tokenExpiry` was used unconditionally here at first, and
   * `verify-whatsapp-tab.tsx` caught what that does: readiness is derived from
   * `lastCheck`, `expiresAt` above prefers the row, and the two are only the
   * same object while nothing has moved between them. A row whose token expired
   * after the last successful check therefore rendered the expired date it
   * prints underneath the words "no expiry" — a green verdict over a dead
   * token, which is the exact failure this whole panel exists to prevent.
   *
   * So the API's own verdict is taken only when it is a verdict on the snapshot
   * whose date is being shown — a check that succeeded *this session*. Otherwise
   * `metaTokenExpiry` is applied to the printed date. That is not a second
   * derivation: it is `@wend/shared`'s function, the one the API calls, run on
   * the value the screen is about.
   */
  const expiry =
    fresh !== null && sessionCheck?.readiness != null
      ? sessionCheck.readiness.tokenExpiry
      : metaTokenExpiry(expiresAt);

  return {
    connected: true,
    failing,
    check,
    stale: failing && check !== null,
    readiness,
    expiresAt,
    expiry,
    /*
     * A date in hand is knowledge. Without one, only an introspected check turns
     * "we hold no date" into "Meta says there is none" — `debug_token` is the
     * one call allowed to fail without failing the check, and when it does,
     * `expiresAt: null` means "not reported".
     */
    expiryKnown: expiresAt !== null || (check?.token.introspected ?? false),
  };
}

/* -------------------------------------------------------------------------- */
/* The status the tab rail draws                                               */
/* -------------------------------------------------------------------------- */

/**
 * Four levels, and `idle` is not a fifth shade of trouble.
 *
 * A workspace that has never connected WhatsApp has nothing wrong with it. Its
 * dot is the absence of a dot; the rail must not nag somebody about a channel
 * they have not chosen to use, or every dot on the rail stops meaning anything.
 */
export const WHATSAPP_TAB_ATTENTION_VALUES = ['idle', 'ok', 'warn', 'bad'] as const;
export type WhatsAppTabAttention = (typeof WHATSAPP_TAB_ATTENTION_VALUES)[number];

/**
 * The tone for each level, so the rail and the panel header tint the same thing
 * the same way. Derived from the list above, never a second switch statement.
 */
export const WHATSAPP_TAB_ATTENTION_TONE: Readonly<Record<WhatsAppTabAttention, BadgeTone>> = {
  idle: 'neutral',
  ok: 'good',
  warn: 'warn',
  bad: 'bad',
};

export interface WhatsAppTabStatus {
  readonly attention: WhatsAppTabAttention;
  /**
   * The words for the dot — **the text alternative, not a decoration.** A dot
   * whose only carrier is its colour tells a colour-blind reader, a screen
   * reader and a forced-colours reader nothing at all, so the rail renders this
   * as visible text or as the dot's accessible name. Never as a tooltip.
   */
  readonly label: string;
  /** One sentence saying what it costs. For the panel, or the rail's title. */
  readonly detail: string;
}

const NOT_CONNECTED: WhatsAppTabStatus = {
  attention: 'idle',
  label: 'Not connected',
  detail: 'No Meta WhatsApp Business account is connected, so nothing can be sent on WhatsApp.',
};

/**
 * The verdict, in the order stated in this file's header.
 *
 * Every branch names a thing that has to be repaired, in the words the panel
 * uses for the same thing — `META_READINESS_DISPLAY` and
 * `META_TOKEN_EXPIRY_DISPLAY` are read rather than paraphrased, so the rail and
 * the panel cannot end up calling one state two names.
 */
export function whatsAppTabStatus(reading: WhatsAppReading): WhatsAppTabStatus {
  if (!reading.connected) return NOT_CONNECTED;

  if (reading.expiry === 'expired') {
    return {
      attention: 'bad',
      label: 'Token expired',
      detail: META_TOKEN_EXPIRY_DISPLAY.expired.meaning,
    };
  }

  if (reading.failing) {
    return {
      attention: 'bad',
      label: 'Not working',
      detail:
        'The last check did not get an answer Meta would stand behind. Nothing can be submitted or sent on WhatsApp until it does.',
    };
  }

  /*
   * A stub answered, so every field below this line was fabricated locally and
   * no Meta account has been verified. The panel says exactly that, in
   * `META_STUB_NOTICE`, in a `warn` Callout — so this is `warn` too, and the
   * tone is taken from the panel rather than chosen again here. The label is
   * the one word that survives being read out on a rail: "WhatsApp, Simulated".
   *
   * Placed after the two red branches and before every other one. See the
   * cascade's own numbering in this file's header for why.
   */
  if (reading.check?.transport === 'stub') {
    return {
      attention: 'warn',
      label: 'Simulated',
      detail: META_STUB_NOTICE,
    };
  }

  if (reading.readiness === null) {
    return {
      attention: 'warn',
      label: 'Not tested',
      detail:
        'Nothing has been read from Meta for this connection yet, so whether it can send is unknown rather than fine.',
    };
  }

  if (!reading.readiness.numberVerified) {
    return {
      attention: 'bad',
      label: META_READINESS_DISPLAY.number_not_verified.label,
      detail: META_READINESS_DISPLAY.number_not_verified.meaning,
    };
  }

  if (reading.readiness.qualityFlagged) {
    return {
      attention: 'bad',
      label: META_READINESS_DISPLAY.quality_flagged.label,
      detail: META_READINESS_DISPLAY.quality_flagged.meaning,
    };
  }

  if (reading.expiry === 'expiring') {
    return {
      attention: 'warn',
      label: 'Token expiring',
      detail: META_TOKEN_EXPIRY_DISPLAY.expiring.meaning,
    };
  }

  if (reading.readiness.wabaPendingReview) {
    return {
      attention: 'warn',
      label: META_READINESS_DISPLAY.waba_pending_review.label,
      detail: META_READINESS_DISPLAY.waba_pending_review.meaning,
    };
  }

  if (reading.readiness.businessUnverified) {
    return {
      attention: 'warn',
      label: 'Business unverified',
      detail:
        'This business is not verified with Meta, so it can reach only a limited number of people a day and cannot raise that limit.',
    };
  }

  if (!reading.expiryKnown) {
    return {
      attention: 'warn',
      label: 'Expiry unknown',
      detail:
        'Meta would not say when this token expires, so Wend cannot warn you before it stops working.',
    };
  }

  return {
    attention: 'ok',
    label: META_READINESS_DISPLAY.ready.label,
    detail: META_READINESS_DISPLAY.ready.meaning,
  };
}
